/* eslint-disable react/jsx-boolean-value, react-hooks/rules-of-hooks,
@typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-call,
@typescript-eslint/no-unsafe-return */
import React, { useState } from 'react';
import {
  TextField, ButtonAction, DateField, Icon, sanitize, AutoCompleteDateField,
  FormatDate,
} from '@honeycomb-npm/honeycomb-react';
import Blade from '../../shared/Blade/Blade';
import { BLADES } from '../../../constants';
import { content } from '../../../utils/contentUtils';
import { validRemIDRegex, validNameRegex, validDateRegex } from '../../../utils/valueUtil';
import { analyticsConstants } from '../../../constants/analyticsConstants';
import './YourDetails.scss';

export interface Props {
  visibility: boolean;
  closeModalOpen: () => void;
  focusDisplay: (blade: string) => void;
  completed: (blade: string) => void;
  formInitialState: any;
  formUpdated: (formInitialState: any) => void;
  validRemId: (formInitialState: any) => void;
  bladeFocus: (blades: any) => void;
}

const yourDetails = ({
  visibility, focusDisplay, completed, formInitialState, formUpdated, bladeFocus, validRemId,
}: Props) => {
  const {
    title, remediationIdLabel, remediationHelpText, remediationErrorMsg, lastNameLabel,
    lastNameErrorMsg, dobLabel, dobHelpText, dobErrorMsg, btnText, remediationInvalidMsg, maxDateErrorMsg,
  } = content[BLADES.yourDetails];

  const {
    remediationId,
    lastName,
    dob,
    remediationIdIsValid,
    lastNameIsValid,
    dobIsValid,
    validateMaxDob,
  } = formInitialState.formData;

  const { isValidRemId } = formInitialState;

  const handleRemErrorMsg = isValidRemId ? remediationErrorMsg : remediationInvalidMsg;

  const handleRemId = (event:any, value: string) => {
    if (!validRemIDRegex(value)) {
      formUpdated({
        ...formInitialState.formData,
        [event]: value.toUpperCase(),
        [`${event}IsValid`]: true,
      });
    }
    if (!isValidRemId) {
      validRemId(true);
    }
  };

  const handleChange = (e:any, v:any) => {
    if (!validNameRegex(v)) {
      formUpdated({ ...formInitialState.formData, [e]: v, [`${e}IsValid`]: true });
    }
  };

  const isValidDate = (day: number, month: number, year: number) => {
    let isValid = false;
    if (day === undefined || month === undefined || year === undefined) {
      return false;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return false;
    }

    if (month === 2) {
      if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
        // Leap year
        isValid = day <= 29;
      } else {
        // Not a leap year
        isValid = day <= 28;
      }
    } else if (month === 4 || month === 6 || month === 9 || month === 11) {
      // April, June, September, November
      isValid = day <= 30;
    } else {
      // All other months
      isValid = day <= 31;
    }
    return isValid;
  };

  const formatDate = (dateString: string): string => {
    const [day, month, year] = dateString.split('/').map(Number);
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    return `${formattedDay}/${formattedMonth}/${year}`;
  };

  const handleDateChange = (e: any, v:any) => {
    const formattedDate = formatDate(v);

    if (validDateRegex(formattedDate)) {
      const [day, month, year] = v.split('/').map(Number); // Convert to numbers
      if (!isValidDate(day, month, year)) {
      // Invalid day for the given month and year
        formUpdated(
          {
            ...formInitialState.formData, dob: v, dobIsValid: false, validateMaxDob: false,
          },
        );
      } else {
        const date = new Date(year, month - 1, day);
        if (date > new Date()) {
        // The date is in the future
          formUpdated({
            ...formInitialState.formData, dob: v, dobIsValid: false, validateMaxDob: true,
          });
        } else {
          formUpdated(
            {
              ...formInitialState.formData, dob: v, dobIsValid: true, validateMaxDob: false,
            },
          );
          if (formInitialState.formData.dob !== v) {
            handleRefresh();
          }
        }
      }
    } else {
      handleRefresh();
      formUpdated(
        {
          ...formInitialState.formData, dob: v, dobIsValid: false, validateMaxDob: false,
        },
      );
    }
  };

  const handleValidate = () => {
    let userData = formInitialState.formData;

    if (!remediationId || remediationId.length < 11) {
      userData = { ...userData, remediationIdIsValid: false };
    }
    if (!lastName.trim()) {
      userData = { ...userData, lastNameIsValid: false };
    }
    if (!dob) {
      userData = { ...userData, dobIsValid: false, validateMaxDob: false };
    }
    if (userData.dobIsValid) {
      userData = { ...userData, dob: formatDate(dob) };
    }

    formUpdated({
      ...formInitialState.formData,
      ...userData,
      accountName: '',
      bsb: '',
      accountNumber: '',
      isConsent: null,
    });
    if (userData.remediationIdIsValid && userData.lastNameIsValid && userData.dobIsValid && !userData.validateMaxDob && isValidRemId) {
      completed(BLADES.yourDetails);
    }
  };

  const handleNext = () => {
    handleValidate();
  };

  const handleRefresh = () => {
    bladeFocus(BLADES.yourDetails);
  };

  return (
    <Blade
      id={BLADES.yourDetails}
      className="your-details"
      shouldVerticalAlign={true}
      visibility={visibility}
      goToPreviousBladeClick={() => focusDisplay(BLADES.getStarted)}
    >
      <div className="your-details__wrapper">
        <div className="container pr-lg-5">
          <div className="col-12">
            <h3>{title}</h3>
          </div>
          <form className="row col-12 col-md-6 col-lg-5">
            <div className="col-12">
              <TextField
                helptext={remediationHelpText}
                label={remediationIdLabel}
                isValid={(remediationIdIsValid && isValidRemId)}
                maxLength={15}
                errormessage={handleRemErrorMsg}
                name="remediationId"
                value={remediationId.toUpperCase()}
                onChange={handleRemId}
                onBlur={handleRefresh}
              />
            </div>
            <div className="col-12">
              <TextField
                label={lastNameLabel}
                isValid={lastNameIsValid}
                name="lastName"
                errormessage={lastNameErrorMsg}
                value={lastName}
                onChange={handleChange}
                onBlur={handleRefresh}
              />
            </div>
            <div className="col-12">
              <AutoCompleteDateField
                errormessage={validateMaxDob ? maxDateErrorMsg : dobErrorMsg}
                helptext={dobHelpText}
                id="datefield"
                isValid={dobIsValid}
                label={dobLabel}
                name="dob"
                value={dob}
                onChange={handleDateChange}
                onBlur={handleRefresh}
              />
            </div>
            <div className="col-12">
              <ButtonAction
                id={analyticsConstants.ofiYourDetails}
                onClick={handleNext}
              >
                {btnText}
              </ButtonAction>
            </div>
          </form>
        </div>
      </div>
    </Blade>
  );
};

export default yourDetails;
